// src/services/supportService.js
import api from "./apiClient";

const CACHE = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 Minutes
const API_URL = "https://utility.wiiz.it/api/admin/support-feedback/dashboard";

/**
 * Fetches support tickets data with caching.
 * 
 * @param {string} timePeriod - Time period for analysis.
 * @param {string} tenantCategory - Category filter (e.g. 'all', 'estrel', 'education').
 * @param {boolean} forceRefresh - Whether to bypass cache.
 * @returns {Promise<Object>} Support tickets and KPIs.
 */
// Update signature to include customRange
export async function fetchSupportTickets(timePeriod = "weekly", tenantCategory = "all", customRange = null, forceRefresh = false) {
    const cacheKey = `SUPPORT_${timePeriod}_${tenantCategory}_${customRange ? JSON.stringify(customRange) : ''}`;
    const cachedEntry = CACHE.get(cacheKey);
    const now = Date.now();

    if (!forceRefresh && cachedEntry && now - cachedEntry.timestamp < CACHE_TTL) {
        console.log("⚡ Returning Cached Support Data for:", cacheKey);
        return cachedEntry.data;
    }

    try {
        console.log(`fetching support data: period=${timePeriod}, category=${tenantCategory}`);

        const payload = {
            tenant_category: tenantCategory
        };

        if (timePeriod === "custom") {
            if (customRange?.start && customRange?.end) {
                payload.start_date = customRange.start;
                payload.end_date = customRange.end;
                // Do NOT send time_period: 'custom' if backend doesn't support it or if dates imply it
            } else {
                console.warn("Skipping Support API call: Custom range incomplete");
                return null;
            }
        } else {
            payload.time_period = timePeriod;
        }

        const response = await api.post(API_URL, payload);

        if (response.data && response.data.status === "success") {
            const apiData = response.data.data;

            const mappedData = {
                kpis: {
                    openTickets: apiData.summary_kpis?.open_tickets || 0,
                    slaBreaches: apiData.summary_kpis?.sla_breaches || 0,
                    avgResolution: apiData.summary_kpis?.avg_resolution || "0 hrs",
                    csatScore: apiData.summary_kpis?.csat_score || "0"
                },
                charts: {
                    categoryDistribution: apiData.tickets_by_category?.map(c => c.count) || [],
                    categoryLabels: apiData.tickets_by_category?.map(c => c.category) || [],
                    resolutionTrend: {
                        categories: apiData.resolution_time_trend?.map(d => d.date) || [],
                        data: apiData.resolution_time_trend?.map(d => d.avg_hours) || []
                    }
                },
                tickets: apiData.recent_tickets?.map(t => ({
                    id: t.ticket_id,
                    tenant: t.tenant,
                    category: t.category,
                    issue: t.issue,
                    priority: t.priority,
                    status: t.status,
                    created: t.created,
                    tenantCategory: "all"
                })) || [],
                sentiment: {
                    positive: apiData.feedback_sentiment_analysis?.positive || 0,
                    neutral: apiData.feedback_sentiment_analysis?.neutral || 0,
                    negative: apiData.feedback_sentiment_analysis?.negative || 0
                },
                rawCategories: apiData.tickets_by_category
            };

            // Cache Update
            CACHE.set(cacheKey, {
                data: mappedData,
                timestamp: Date.now()
            });

            return mappedData;
        }
    } catch (error) {
        console.error("Support API Error:", error);
    }
    return null;
}
